'use client';

import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { createBrowserClient } from '@/lib/supabase-clients';

interface EmailVerifyInlineProps {
  /** Pending email address (auth.users.email — not yet verified). */
  email: string;
  /** Called after a successful verify (and the post-verify hook) when
   *  reloadOnSuccess is false — hosts advance their own UI (local flip +
   *  router.refresh, mirroring the phone step). */
  onVerified?: () => void;
  /** Banner mode: full page reload after verify (the layout re-reads
   *  email_confirmed_at server-side and drops the banner). Embedded mounts
   *  (LaunchPath step, dialogs) MUST leave this false — a reload strands the
   *  overlay behind its "seen once" key. */
  reloadOnSuccess?: boolean;
  /** Tighter spacing for dialog/step embeds. */
  compact?: boolean;
}

/**
 * The ONE email-OTP surface — extracted from VerifyEmailBanner (Plan B wave,
 * 2026-07-26) so the launch ladder, the channels dialog, and the banner all
 * share a single battle-tested verify path instead of growing copies.
 *
 * Verification mechanics preserved verbatim from the banner:
 *  - verifyOtp type 'email_change' first (Pattern A: anonymous→updateUser
 *    flow), retry as type 'email' on invalid/expired (normal signups).
 *  - POST /api/auth/post-verify-hook after success (session invalidation of
 *    OTHER sessions + converted_at stamp) — fire-and-forget.
 * New over the banner:
 *  - An "already confirmed/verified" response resolves as SUCCESS (a race
 *    with another tab or a stale panel is a win, not an error).
 *  - Change-email updates the address codes are checked against (the banner
 *    kept verifying against the original prop).
 *
 * No-reload contract: this component uses the singleton browser client, so a
 * successful verifyOtp fires USER_UPDATED into SupabaseAuthContext
 * synchronously — authUser.email_confirmed_at is fresh for every consumer
 * (including the stashed-invite auto-fire) without any reload.
 */
export function EmailVerifyInline({
  email,
  onVerified,
  reloadOnSuccess = false,
  compact = false,
}: EmailVerifyInlineProps) {
  // Codes are checked against the address they were SENT to — change-email
  // moves the target.
  const [currentEmail, setCurrentEmail] = useState(email);

  const [otpCode, setOtpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);

  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);

  const [showChangeForm, setShowChangeForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [changing, setChanging] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [changeSuccess, setChangeSuccess] = useState<string | null>(null);

  const finishSuccess = async () => {
    // Post-verify hook: invalidates OTHER sessions + stamps converted_at —
    // the current session survives by design (scope 'others').
    try {
      await fetch('/api/auth/post-verify-hook', { method: 'POST' });
    } catch {
      // Non-fatal — the auth callback path runs the same logic later.
    }
    setVerified(true);
    if (reloadOnSuccess) {
      setTimeout(() => window.location.reload(), 600);
    } else {
      onVerified?.();
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) return;

    setVerifying(true);
    setVerifyError(null);
    try {
      const supabase = createBrowserClient();

      let { error } = await supabase.auth.verifyOtp({
        email: currentEmail,
        token: otpCode,
        type: 'email_change',
      });

      if (error && /invalid|expired/i.test(error.message)) {
        const retry = await supabase.auth.verifyOtp({
          email: currentEmail,
          token: otpCode,
          type: 'email',
        });
        error = retry.error;
      }

      if (error) {
        // A race is a win: another tab / an earlier code already confirmed.
        if (/already.*(confirm|verif)/i.test(error.message)) {
          await finishSuccess();
          return;
        }
        setVerifyError(error.message || 'Invalid or expired code');
        return;
      }

      await finishSuccess();
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Failed to verify');
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendStatus('idle');
    setResendError(null);
    try {
      const res = await fetch('/api/auth/resend-verification', { method: 'POST' });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
      };
      if (!res.ok) {
        setResendStatus('error');
        setResendError(body.message || body.error || 'Failed to resend');
        return;
      }
      setResendStatus('sent');
    } catch (err) {
      setResendStatus('error');
      setResendError(err instanceof Error ? err.message : 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  const handleChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setChanging(true);
    setChangeError(null);
    setChangeSuccess(null);
    try {
      const res = await fetch('/api/auth/change-pending-email', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newEmail }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        newEmail?: string;
      };
      if (!res.ok) {
        setChangeError(body.message || body.error || 'Failed to update email');
        return;
      }
      const updated = body.newEmail || newEmail;
      setCurrentEmail(updated);
      setOtpCode('');
      setChangeSuccess(`We sent a new verification code to ${updated}.`);
      setShowChangeForm(false);
    } catch (err) {
      setChangeError(err instanceof Error ? err.message : 'Failed to update email');
    } finally {
      setChanging(false);
    }
  };

  if (verified) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
        <Check className="h-4 w-4" />
        {reloadOnSuccess ? 'Email verified. Refreshing…' : 'Email verified.'}
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {/* OTP code input — primary action */}
      <form onSubmit={handleVerifyOtp} className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          placeholder="000000"
          value={otpCode}
          onChange={(e) => {
            const clean = e.target.value.replace(/\D/g, '').slice(0, 6);
            setOtpCode(clean);
            setVerifyError(null);
          }}
          disabled={verifying}
          className={cn(
            'h-9 w-32 rounded-md border bg-background px-3 font-mono text-center tracking-widest',
            'text-base text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
            verifyError ? 'border-red-400 dark:border-red-700' : 'border-input',
          )}
        />
        <Button type="submit" size="sm" disabled={verifying || otpCode.length !== 6} className="h-9">
          {verifying ? (
            <>
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Verifying…
            </>
          ) : (
            'Verify'
          )}
        </Button>

        {verifyError && (
          <span className="text-xs text-red-700 dark:text-red-300">{verifyError}</span>
        )}
      </form>

      {/* Secondary actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="outline" onClick={handleResend} disabled={resending} className="h-8">
          {resending ? (
            <>
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Resending…
            </>
          ) : (
            'Resend code'
          )}
        </Button>

        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowChangeForm((v) => !v)}
          className="h-8"
        >
          Change email address
        </Button>

        {resendStatus === 'sent' && (
          <span className="text-xs text-muted-foreground">New code sent. Check your inbox.</span>
        )}
        {resendStatus === 'error' && resendError && (
          <span className="text-xs text-red-700 dark:text-red-300">{resendError}</span>
        )}
        {changeSuccess && (
          <span className="text-xs text-muted-foreground">{changeSuccess}</span>
        )}
      </div>

      {/* Inline change-email form */}
      {showChangeForm && (
        <form onSubmit={handleChange} className="flex flex-wrap items-center gap-2">
          <input
            type="email"
            required
            placeholder="new@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className={cn(
              'h-8 rounded-md border border-input bg-background px-3',
              'text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
            )}
            disabled={changing}
          />
          <Button type="submit" size="sm" disabled={changing} className="h-8">
            {changing ? (
              <>
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                Updating…
              </>
            ) : (
              'Send new code'
            )}
          </Button>
          {changeError && (
            <span className="text-xs text-red-700 dark:text-red-300">{changeError}</span>
          )}
        </form>
      )}
    </div>
  );
}
